import { Box, Chip, Typography } from "@mui/material";
import { ReactNode } from "react";

import { Cartao, listaPt, listrado, num } from "./comum";
import { MOTIVO_DESLIGAMENTO, rotuloOuCodigo } from "./rotulos";
import { Estimativa, Previsto } from "./types";

type Carga = Previsto["cargaDoProximoMes"];
type Prox = ReturnType<typeof proximoMes>;

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** Mês seguinte ao de `dataCiclo` (AAAA-MM-DD), por aritmética de texto: sem Date, sem fuso. */
export function proximoMes(dataCiclo: string): { nome: string; curto: string; mes: string } {
  const a = Number(dataCiclo.slice(0, 4));
  const m = Number(dataCiclo.slice(5, 7));
  const [ano, mes] = m === 12 ? [a + 1, 1] : [a, m + 1];
  const mm = String(mes).padStart(2, "0");
  return { nome: MESES[mes - 1], curto: `${mm}/${String(ano).slice(2)}`, mes: `${ano}-${mm}` };
}

/** Compara os valores já arredondados, como a tela os mostra. */
export function foraDoNormal(e: Estimativa): "acima" | "abaixo" | null {
  const u = Math.round(e.ultimos3);
  if (u > Math.round(e.p75)) return "acima";
  if (u < Math.round(e.p25)) return "abaixo";
  return null;
}

export type MotivoAgrupado = { chave: string; rotulo: string; total: number; tipico: number; protegido: boolean };

export function agruparMotivos(porMotivo: Record<string, number>, meses: number, protegidos: string[]): MotivoAgrupado[] {
  if (meses <= 0) return [];
  const item = (chave: string, rotulo: string, total: number, protegido: boolean): MotivoAgrupado =>
    ({ chave, rotulo, total, tipico: Math.round(total / meses), protegido });
  const prot = protegidos
    .filter((c) => (porMotivo[c] ?? 0) > 0)
    .map((c) => item(c, rotuloOuCodigo(MOTIVO_DESLIGAMENTO, c), porMotivo[c], true));
  const outros = Object.entries(porMotivo)
    .filter(([c, t]) => t > 0 && !protegidos.includes(c))
    .sort((x, y) => y[1] - x[1])
    .map(([c, t]) => item(c, rotuloOuCodigo(MOTIVO_DESLIGAMENTO, c), t, false));
  const resto = outros.slice(4).reduce((s, o) => s + o.total, 0);
  return [...prot, ...outros.slice(0, 4), ...(resto > 0 ? [item("outros", "Outros motivos", resto, false)] : [])];
}

const COR_PROTEGIDO = ["#3024E1", "#6A61EC", "#9A94F2"];
const COR_OUTRO = ["#B4500A", "#E09A63", "#8A8A99", "#5C5C6E"];

function Serie({ id, rotulo, e, cor, prox }: { id: string; rotulo: string; e: Estimativa; cor: string; prox: Prox }) {
  const est = Math.round(e.mediana);
  const max = Math.max(1, est, ...e.serie.map((s) => s.n));
  const col = (mes: string, valor: number, texto: string, curto: string, estimativa?: boolean) => (
    <Box key={mes} data-mes={mes} data-estimativa={estimativa ? "true" : undefined} display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end" flex={1} minWidth={0} gap={0.5}>
      <Typography variant="caption" fontWeight={600}>{texto}</Typography>
      <Box sx={{ width: "100%", maxWidth: 28, height: Math.max(valor ? 2 : 0, (valor / max) * 80), borderRadius: 0.5, background: estimativa ? listrado(cor) : cor }} />
      <Typography variant="caption" color="text.secondary">{curto}</Typography>
    </Box>
  );
  return (
    <Box role="img" aria-label={`${rotulo} por mês nos meses cobertos e a estimativa de ${prox.nome}`} data-testid={`serie-${id}`} display="flex" alignItems="flex-end" gap={0.5}>
      {e.serie.map((s) => col(s.mes, s.n, num(s.n), `${s.mes.slice(5, 7)}/${s.mes.slice(2, 4)}`))}
      {col(prox.mes, est, `≈${num(est)}`, prox.curto, true)}
    </Box>
  );
}

function CartaoCarga({ id, titulo, rotulo, e, cor, prox, fazer, lado, children }: {
  id: "admissoes" | "desligamentos"; titulo: string; rotulo: string; e: Estimativa; cor: string; prox: Prox;
  fazer: string; lado?: string; children?: ReactNode;
}) {
  const f = foraDoNormal(e);
  const base = e.meses === 1 ? "1 mês" : `${e.meses} meses`;
  const desde = e.meses < 12 && e.serie[0]
    ? `: o eSocial guardado começa em ${MESES[Number(e.serie[0].mes.slice(5, 7)) - 1]} de ${e.serie[0].mes.slice(0, 4)}`
    : "";
  return (
    <Box data-testid={`cartao-${id}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography component="h4" variant="subtitle1" fontWeight={700}>{titulo}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
          <Chip size="small" variant="outlined" label="estimativa" />
        </Box>
      </Box>
      <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
        <Typography sx={{ fontFamily: "Sora", fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, color: cor }}>{`cerca de ${num(Math.round(e.mediana))}`}</Typography>
        {lado && <Typography variant="body2" color="text.secondary">{lado}</Typography>}
      </Box>
      <Typography variant="body2">
        {`Na maioria dos meses, entre ${num(Math.round(e.p25))} e ${num(Math.round(e.p75))}.${f ? ` Os últimos 3 meses deram ${num(Math.round(e.ultimos3))}, ${f} do normal.` : ""}`}
      </Typography>
      <Serie id={id} rotulo={id === "admissoes" ? "Admissões" : "Desligamentos"} e={e} cor={cor} prox={prox} />
      {children}
      <Typography variant="body2" color="text.secondary">{`Mediana de ${base}${desde}. ${fazer}`}</Typography>
    </Box>
  );
}

export function ProximoMes({ dataCiclo, carga, motivosQueANormaProtege }: { dataCiclo: string; carga: Carga; motivosQueANormaProtege: string[] }) {
  const { admissoes: a, desligamentos: d } = carga;
  if (a.meses === 0 && d.meses === 0) return null;
  const prox = proximoMes(dataCiclo);
  const motivos = d.meses > 0 ? agruparMotivos(d.porMotivo, d.meses, motivosQueANormaProtege) : [];
  let ip = 0;
  let io = 0;
  const cores = motivos.map((m) => (m.chave === "outros" ? "#C9C9D6" : m.protegido ? COR_PROTEGIDO[ip++ % 3] : COR_OUTRO[io++ % 4]));
  return (
    <Cartao titulo={`Próximo mês: ${prox.nome}`} subtitulo="O que a história dos meses sugere. Os contratos que vencem estão na tabela acima: vencer não é sair.">
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
        {a.meses > 0 && (
          <CartaoCarga id="admissoes" titulo="Entradas" rotulo="Admissões esperadas" e={a} cor="#3024E1" prox={prox}
            fazer="Prepare as inclusões para entrarem no plano dentro do prazo." />
        )}
        {d.meses > 0 && (
          <CartaoCarga id="desligamentos" titulo="Saídas" rotulo="Desligamentos esperados" e={d} cor="#B4500A" prox={prox}
            lado={`${(d.taxa * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% dos vínculos`}
            fazer="Prepare as exclusões para não pagar por quem já saiu.">
            {motivos.length > 0 && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2" color="text.secondary">Por motivo, num mês típico</Typography>
                <Box display="flex" sx={{ height: 14, borderRadius: 1, overflow: "hidden" }}>
                  {motivos.map((m, i) => <Box key={m.chave} sx={{ flex: m.total, bgcolor: cores[i] }} />)}
                </Box>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.5}>
                  {motivos.map((m, i) => (
                    <Box key={m.chave} data-testid={`motivo-${m.chave}`} data-protegido={String(m.protegido)} display="flex" alignItems="center" gap={0.75}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: cores[i], flexShrink: 0 }} />
                      <Typography variant="body2" flex={1}>{m.rotulo}</Typography>
                      <Typography variant="body2" fontWeight={600}>{num(m.tipico)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {d.comDireito !== null && (
              <Box data-testid="com-direito" sx={{ bgcolor: "#EEEDFD", borderRadius: 1, p: 1.5 }}>
                <Typography variant="body2">
                  {`Cerca de ${num(Math.round(d.comDireito))} por mês saem com direito de continuar no plano, se pagavam parte dele (tons de azul).${motivosQueANormaProtege.length > 0 ? ` Pela norma: ${listaPt(motivosQueANormaProtege.map((c) => rotuloOuCodigo(MOTIVO_DESLIGAMENTO, c).toLocaleLowerCase("pt-BR")))}.` : ""}`}
                </Typography>
              </Box>
            )}
          </CartaoCarga>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" data-testid="sem-sazonalidade">
        A barra listrada é a estimativa. Variação por época do ano entra quando houver dois anos de história do eSocial.
      </Typography>
    </Cartao>
  );
}
